import { useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { FormattedMessage, useIntl, IntlShape } from "react-intl";
import { useParams } from "react-router-dom";
import { Col, Row } from "react-bootstrap";
import { toast } from "react-toastify";

import Dialog from "components/organisms/Dialog/Dialog";
import Button from "components/atoms/Button/Button";
import SearchSelect from "pages/shipper/RequestForm/components/Common/SearchSelect";
import Textarea from "components/atoms/Textarea/Textarea";
import { declineRequest } from "api";
import errorToast from "helpers/errorToast";
import testIds from "constants/testIds";

interface IDeclineProps {
  reason?: string;
  comment?: string;
}

interface IQuotationDeclineDialogProps {
  isOpen: boolean;
  close: () => void;
  getQuoteRequest: Function;
}

const declineOptions = (intl: IntlShape) => [
  {
    label: intl.formatMessage({ id: "quote.decline.toBusyShipment", defaultMessage: "Too busy with other shipment" }),
    value: "too_busy",
  },
  {
    label: intl.formatMessage({
      id: "quote.decline.shipmentNotFit",
      defaultMessage: "Shipment is not a fit for our company",
    }),
    value: "not_fit_for_company",
  },
  {
    label: intl.formatMessage({
      id: "quote.decline.shipmentNotInteresting",
      defaultMessage: "Shipment is not interesting enough",
    }),
    value: "not_interesting",
  },
  {
    label: intl.formatMessage({ id: "quote.decline.other", defaultMessage: "Other" }),
    value: "other",
  },
];

function QuotationDeclineDialog(props: IQuotationDeclineDialogProps) {
  const { isOpen, close, getQuoteRequest } = props;
  const { id } = useParams();
  const intl = useIntl();
  const methods = useForm();
  const { handleSubmit, setValue } = methods;

  const [isLoading, setIsLoading] = useState(false);

  const handleDeclineRequest = (data: IDeclineProps) => {
    setIsLoading(true);
    declineRequest(id, data)
      .then(() => {
        toast(
          intl.formatMessage({
            id: "quote.declineSuccessMsg",
            defaultMessage: "Quote request declined successfully",
          }),
          { type: "success" },
        );
        getQuoteRequest();
        setValue("reason", "");
        setValue("comment", "");
        close();
      })
      .catch((error) => errorToast(error))
      .finally(() => setIsLoading(false));
  };

  return (
    <Dialog
      size="sm"
      title={intl.formatMessage({
        id: "quote.declineThisQuoteRequest.declineThisQuoteRequest",
        defaultMessage: "Decline this quote request",
      })}
      show={isOpen}
      onHide={close}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(handleDeclineRequest)}>
          <div style={{ maxWidth: 400 }}>
            <p className="text-secondary font-b1">
              <FormattedMessage
                id="quote.declineThisQuoteRequest"
                defaultMessage="Decline to quote if you are not able to fulfil requested shipment,the shipper will be notified.
                    You can reopen the request, Before the quoting deadline ends."
              />
            </p>
          </div>
          <div style={{ maxWidth: 316 }} className="m-auto">
            <div className="mt-5">
              <SearchSelect
                options={declineOptions(intl)}
                name="reason"
                label={<FormattedMessage id="quote.reasonForDecline" defaultMessage="Reason for declining" />}
                placeholder={intl.formatMessage({
                  id: "general.pleaseSelect",
                  defaultMessage: "Please select",
                })}
                required
              />
            </div>
            <div className="mt-3">
              <label className="required">
                <FormattedMessage id="general.comments" defaultMessage="Comments" />
              </label>
              <Textarea
                className="font-b3"
                eventContext="Cancel Request Dialog"
                name="comment"
                placeholder="Please provide your comment. Your feedback is important for us."
                validations={{ minLength: { value: 3, message: "Please enter at least 3 characters" } }}
                required
              />
            </div>
          </div>
          <Row className="mt-5">
            <Col lg={12} className="d-flex justify-content-between">
              <Button
                variant="outline-primary"
                label={<FormattedMessage id="general.cancel" defaultMessage="Cancel" />}
                size="lg"
                onClick={close}
                data-testid={testIds.SHIPMENT_DASHBOARD_DECLINE_MODAL_CLOSE_BUTTON}
              />
              <Button
                variant="primary"
                type="submit"
                size="lg"
                label={intl.formatMessage({ id: "general.decline", defaultMessage: "Decline" })}
                data-testid={testIds.SHIPMENT_DASHBOARD_DECLINE_BUTTON}
                disabled={isLoading}
              />
            </Col>
          </Row>
        </form>
      </FormProvider>
    </Dialog>
  );
}

export default QuotationDeclineDialog;
